import React, { useMemo, useState } from "react"

import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import moment from "moment-timezone"

import { PayerStatus } from "@common-shared/config/oneVerify/oneVerifyPayerStatus.config"
import type { IOneVerifyPayerStatusIncident } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import {
    NIRVANA_COLORS,
    NIRVANA_THEME,
} from "@front-shared/config/theme.config"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { useIsMobile } from "@front-shared/hooks/isMobile.hook"

import {
    PAYER_STATUS_CONFIG,
    PayerStatusRange,
} from "../../config/payerStatus.config"
import { SelectTabsComponent } from "../../elements/selectTabs.component"
import { PayerStatusHelper } from "../../helpers/payerStatus.helper"

export const PayerStatusTimelineComponent: React.FunctionComponent<
    IPayerStatusTimelineComponentProps
> = props => {
    const isMobile = useIsMobile()

    // Fewer time labels fit a narrower axis
    const isTablet = useIsMobile(NIRVANA_THEME.mediaQueries.md)
    const isSmallDesktop = useIsMobile(NIRVANA_THEME.mediaQueries.lg)

    const [range, setRange] = useState<PayerStatusRange>(
        PAYER_STATUS_CONFIG.defaultRange,
    )

    const [isExpanded, setIsExpanded] = useState<boolean>(false)

    const operationalConfig =
        PAYER_STATUS_CONFIG.statuses[PayerStatus.OPERATIONAL]

    const issueConfig = PAYER_STATUS_CONFIG.statuses[PayerStatus.ISSUE_DETECTED]

    const rangeLabel = PAYER_STATUS_CONFIG.rangeLabels[range]

    const rangeStart = PayerStatusHelper.getRangeStart(range, props.fetchedAt)

    const incidents = useMemo<IOneVerifyPayerStatusIncident[]>(() => {
        return PayerStatusHelper.getIncidentsInRange(
            props.incidents,
            rangeStart,
        )
    }, [props.incidents, rangeStart])

    const ongoingCount = incidents.filter(
        incident => !incident.recoveredAt,
    ).length

    // One lane per payer, incidents come ongoing first, then newest, so lanes keep that order
    // Map keeps insertion order, object would put numeric payer IDs first
    const lanes = useMemo<IOneVerifyPayerStatusIncident[][]>(() => {
        const incidentsByPayerId = new Map<
            string,
            IOneVerifyPayerStatusIncident[]
        >()

        for (const incident of incidents) {
            incidentsByPayerId.set(incident.payerId, [
                ...(incidentsByPayerId.get(incident.payerId) || []),
                incident,
            ])
        }

        return [...incidentsByPayerId.values()]
    }, [incidents])

    const shownLanes = isExpanded
        ? lanes
        : lanes.slice(0, PAYER_STATUS_CONFIG.timelineMaxPayers)

    // Range start and points between it and now, now is labeled separately
    const axisLabels = useMemo<{ fraction: number; label: string }[]>(() => {
        const fractions = isTablet
            ? [0]
            : isSmallDesktop
              ? [0, 0.5]
              : [0, 0.25, 0.5, 0.75]

        return fractions.map(fraction => {
            // Within a day range only range start shows the day, later labels read on from it
            const format =
                range === PayerStatusRange.ONE_WEEK
                    ? PAYER_STATUS_CONFIG.dayFormat
                    : range === PayerStatusRange.ONE_DAY && fraction === 0
                      ? PAYER_STATUS_CONFIG.dayTimeFormat
                      : PAYER_STATUS_CONFIG.timeFormat

            return {
                fraction,
                label: moment(
                    rangeStart + fraction * (props.fetchedAt - rangeStart),
                ).format(format),
            }
        })
    }, [range, props.fetchedAt, isTablet, isSmallDesktop])

    // Position in % of range, incidents that started before it are cut at its start
    const getPosition = (timestamp: number): number => {
        return (
            ((Math.max(timestamp, rangeStart) - rangeStart) /
                (props.fetchedAt - rangeStart)) *
            100
        )
    }

    const renderLane = (
        laneIncidents: IOneVerifyPayerStatusIncident[],
    ): React.ReactNode => {
        // Ongoing incident goes first
        const [latestIncident] = laneIncidents

        const payerName = PayerStatusHelper.getPayerName(latestIncident)

        const isOngoing = !latestIncident.recoveredAt

        const statusLabel = isOngoing
            ? `Ongoing · ${PayerStatusHelper.getIncidentDurationLabel(latestIncident, props.fetchedAt)}`
            : `${PayerStatusHelper.getIssuesCountLabel(laneIncidents.length)}, resolved`

        const statusColor = isOngoing
            ? issueConfig.color
            : NIRVANA_COLORS.brand.grey7

        return (
            <button
                key={latestIncident.payerId}
                type="button"
                aria-haspopup="dialog"
                aria-label={`${payerName}, payer ID ${latestIncident.payerId}, ${statusLabel}, issue detected ${laneIncidents
                    .map(incident =>
                        PayerStatusHelper.getIncidentPeriodLabel(incident),
                    )
                    .join("; ")}. Open details`}
                className="grid w-full grid-cols-[200px_minmax(0,1fr)_150px] items-center gap-x-[20px] rounded-[10px] border-0 bg-transparent px-[12px] py-0 text-left hover:bg-brand-warmLight025 sm:grid-cols-1"
                onClick={() => {
                    props.onPayerClick(latestIncident.payerId)
                }}
            >
                <div className="flex min-w-0 items-start gap-[8px] pb-[6px] pt-[10px]">
                    {/*Many payer names start with the same words, so the full name wraps instead of being cut*/}
                    <div className="min-w-0 flex-1">
                        <PrimaryText
                            typography="textSemibold"
                            className="break-words"
                        >
                            {payerName}
                        </PrimaryText>

                        <PrimaryText
                            typography="caption"
                            className="opacity-60"
                        >
                            {latestIncident.payerId}
                        </PrimaryText>
                    </div>

                    {isMobile && (
                        <PrimaryText
                            typography="captionSemibold"
                            className="shrink-0"
                            color={statusColor}
                        >
                            {statusLabel}
                        </PrimaryText>
                    )}
                </div>

                {/*Grows with a wrapped payer name, so grid lines stay continuous and bars centered*/}
                <div className="relative min-h-[40px] self-stretch sm:min-h-[22px]">
                    {[25, 50, 75].map(left => (
                        <span
                            key={left}
                            className="absolute bottom-0 top-0 w-[1px] bg-brand-warmLight2"
                            style={{ left: `${left}%` }}
                        />
                    ))}

                    {/*Range ends now*/}
                    <span className="absolute bottom-0 right-0 top-0 w-[2px] bg-brand-warmShadow" />

                    <div className="absolute left-0 right-0 top-1/2 h-[10px] -translate-y-1/2">
                        {laneIncidents.map(incident => {
                            const left = getPosition(incident.startedAt)

                            const width =
                                getPosition(
                                    incident.recoveredAt || props.fetchedAt,
                                ) - left

                            return (
                                <span
                                    key={incident.id}
                                    className="absolute top-0 h-full rounded-full"
                                    title={PayerStatusHelper.getIncidentPeriodLabel(
                                        incident,
                                    )}
                                    style={{
                                        // Keep very short incidents visible, but inside the bar
                                        left: `min(${left}%, calc(100% - 6px))`,
                                        width: `max(${width}%, 6px)`,
                                        backgroundColor: issueConfig.color,
                                    }}
                                />
                            )
                        })}
                    </div>
                </div>

                {!isMobile && (
                    <PrimaryText
                        typography="textSmallSemibold"
                        className="whitespace-nowrap text-right"
                        color={statusColor}
                    >
                        {statusLabel}
                    </PrimaryText>
                )}
            </button>
        )
    }

    return (
        <section
            aria-label="Issue timeline"
            className={`flex flex-col gap-[18px] rounded-[16px] bg-brand-white p-[24px] shadow-sm ${
                props.className || ""
            }`}
        >
            <div className="flex flex-col gap-[14px]">
                <div className="flex flex-wrap items-center justify-between gap-[12px]">
                    <PrimaryText typography="h5">Issue timeline</PrimaryText>

                    {/*Pills are wider than mobile screen, so they scroll*/}
                    <div className="max-w-full overflow-x-auto">
                        <SelectTabsComponent
                            items={Object.values(PayerStatusRange).map(
                                value => ({
                                    value,
                                    label: PAYER_STATUS_CONFIG.rangeLabels[
                                        value
                                    ],
                                }),
                            )}
                            value={range}
                            onChange={setRange}
                            ariaLabel="Time range for issue timeline"
                        />
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-x-[12px] gap-y-[8px]">
                    <div className="flex flex-wrap items-center gap-x-[6px]">
                        <PrimaryText typography="textSmallSemibold">
                            {incidents.length
                                ? `${PayerStatusHelper.getIssuesCountLabel(incidents.length)} in the last ${rangeLabel}`
                                : `No issues in the last ${rangeLabel}`}
                        </PrimaryText>

                        {!!incidents.length && (
                            <>
                                <PrimaryText
                                    typography="textSmall"
                                    color={operationalConfig.color}
                                >
                                    {`· ${incidents.length - ongoingCount} resolved`}
                                </PrimaryText>

                                <PrimaryText
                                    typography="textSmall"
                                    color={issueConfig.color}
                                >
                                    {`· ${ongoingCount} ongoing`}
                                </PrimaryText>
                            </>
                        )}
                    </div>

                    <div className="flex items-center gap-[8px]">
                        <span
                            className="h-[8px] w-[20px] rounded-full"
                            style={{ backgroundColor: issueConfig.color }}
                        />

                        <PrimaryText
                            typography="textSmall"
                            className="opacity-60"
                        >
                            {issueConfig.label}
                        </PrimaryText>
                    </div>
                </div>
            </div>

            {lanes.length ? (
                // Lanes go into card padding, so their hover background doesn't end right at the text
                <div className="-mx-[12px] flex flex-col">
                    <div
                        aria-hidden="true"
                        className="grid grid-cols-[200px_minmax(0,1fr)_150px] items-end gap-x-[20px] border-0 border-b border-solid border-brand-warmLight2 px-[12px] py-[8px] sm:grid-cols-1"
                    >
                        {!isMobile && (
                            <PrimaryText
                                typography="captionSemibold"
                                className="opacity-60"
                            >
                                Payer
                            </PrimaryText>
                        )}

                        <div className="relative h-[18px]">
                            {axisLabels.map(item => (
                                <span
                                    key={item.fraction}
                                    className="absolute bottom-0 whitespace-nowrap"
                                    style={{
                                        left: `${item.fraction * 100}%`,
                                        // Range start label begins at its point, others are centered on it
                                        transform:
                                            item.fraction === 0
                                                ? undefined
                                                : "translateX(-50%)",
                                    }}
                                >
                                    <PrimaryText
                                        typography="caption"
                                        className="opacity-60"
                                    >
                                        {item.label}
                                    </PrimaryText>
                                </span>
                            ))}

                            <span className="absolute bottom-0 right-0 whitespace-nowrap">
                                <PrimaryText typography="captionSemibold">
                                    Now
                                </PrimaryText>
                            </span>
                        </div>

                        {!isMobile && (
                            <PrimaryText
                                typography="captionSemibold"
                                className="text-right opacity-60"
                            >
                                Status
                            </PrimaryText>
                        )}
                    </div>

                    {shownLanes.map(renderLane)}

                    {lanes.length > PAYER_STATUS_CONFIG.timelineMaxPayers && (
                        <div className="flex justify-center border-0 border-t border-solid border-brand-warmLight2 pt-[12px]">
                            <button
                                type="button"
                                aria-expanded={isExpanded}
                                className="flex items-center gap-[8px] whitespace-nowrap rounded-full border-0 bg-transparent px-[16px] py-[8px] hover:bg-brand-warmLight025"
                                onClick={() => {
                                    setIsExpanded(current => !current)
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={
                                        isExpanded ? faChevronUp : faChevronDown
                                    }
                                    className="text-[12px]"
                                    aria-hidden="true"
                                />

                                <PrimaryText typography="textSemibold">
                                    {isExpanded
                                        ? "Show fewer payers"
                                        : `Show all ${lanes.length} payers`}
                                </PrimaryText>
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex items-center gap-[10px] py-[8px]">
                    <FontAwesomeIcon
                        icon={operationalConfig.icon}
                        style={{ color: operationalConfig.color }}
                    />

                    <PrimaryText>
                        {`No issues detected in the last ${rangeLabel}.`}
                    </PrimaryText>
                </div>
            )}
        </section>
    )
}

interface IPayerStatusTimelineComponentProps {
    className?: string
    incidents: IOneVerifyPayerStatusIncident[] // all of history, ongoing first, then newest
    fetchedAt: number
    onPayerClick: (payerId: string) => void
}
